import { useState, useEffect } from "react";
import ApiService from "../../services/api";
import Modal from "../ui/Modal";

const ClientModal = ({ client, onSave, onClose, invoices = [], quotes = [], settings, documents = [], onUploadDocument, onDeleteDocument, onUpdateDocument, onDownloadDocument }) => {
  const [formData, setFormData] = useState({
    _id: client?._id || '',
    name: client?.name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    address: client?.address || '',
    // Enhanced client fields
    companyNumber: client?.companyNumber || '',
    vatNumber: client?.vatNumber || '',
    website: client?.website || '',
    contactPerson: client?.contactPerson || '',
    category: client?.category || 'General',
    paymentTerms: client?.paymentTerms || settings?.defaultPaymentTerms || 'Net 30',
    currency: client?.currency || settings?.currency || '£',
    notes: client?.notes || '',
    isActive: client?.isActive !== undefined ? client.isActive : true,
    defaultAutoPayEnabled: Boolean(client?.defaultAutoPayEnabled),
    password: client?.password || ''
  });

  const [activeTab, setActiveTab] = useState('details');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showAddRecipientForm, setShowAddRecipientForm] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [recipientFormData, setRecipientFormData] = useState({ name: '', email: '', isPrimary: false, isActive: true });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Recipients API functions
  const fetchRecipients = async (clientId) => {
    if (!clientId) return;
    
    setLoadingRecipients(true);
    try {
      const result = await ApiService.request(`/client-email-recipients/client/${clientId}`);
      setRecipients(result.data || []);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      alert('Failed to load recipients: ' + error.message);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const saveRecipient = async (recipientData) => {
    try {
      const method = editingRecipient ? 'PUT' : 'POST';
      const url = editingRecipient 
        ? `/client-email-recipients/${editingRecipient._id}`
        : '/client-email-recipients';
      
      const response = await ApiService.request(url, {
        method,
        body: {
          ...recipientData,
          clientId: client._id
        }
      });

      console.log('Recipient saved:', response);
      
      await fetchRecipients(client._id);
      setShowAddRecipientForm(false);
      setEditingRecipient(null);
      setRecipientFormData({ name: '', email: '', isPrimary: false, isActive: true });
    } catch (error) {
      console.error('Error saving recipient:', error);
      alert('Failed to save recipient: ' + error.message);
    }
  };

  const deleteRecipient = async (recipientId) => {
    if (recipients.length <= 1) {
      alert('Cannot delete the last recipient. At least one recipient must exist.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this recipient?')) {
      return;
    }
    
    try {
      await ApiService.request(`/client-email-recipients/${recipientId}`, {
        method: 'DELETE'
      });
      
      await fetchRecipients(client._id);
    } catch (error) {
      console.error('Error deleting recipient:', error);
      alert('Failed to delete recipient: ' + error.message);
    }
  };

  const handleRecipientSubmit = (e) => {
    e.preventDefault();
    if (!recipientFormData.name.trim() || !recipientFormData.email.trim()) {
      alert('Please fill in both name and email fields.');
      return;
    }
    saveRecipient(recipientFormData);
  };

  const startEditRecipient = (recipient) => {
    setEditingRecipient(recipient);
    setRecipientFormData({
      name: recipient.name,
      email: recipient.email,
      isPrimary: Boolean(recipient.isPrimary),
      isActive: Boolean(recipient.isActive)
    });
    setShowAddRecipientForm(true);
  };

  const cancelRecipientForm = () => {
    setShowAddRecipientForm(false);
    setEditingRecipient(null);
    setRecipientFormData({ name: '', email: '', isPrimary: false, isActive: true });
  };

  // Load recipients when client changes or recipients tab is accessed
  useEffect(() => {
    if (client?._id && activeTab === 'recipients') {
      fetchRecipients(client._id);
    }
  }, [client?._id, activeTab]);

  const formatCurrency = (amount) => `${formData.currency}${parseFloat(amount || 0).toFixed(2)}`;
  const formatDate = (date) => new Date(date).toLocaleDateString();
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Calculate client statistics
  const clientInvoices = invoices.filter(inv => inv.clientId === client?._id) || [];
  const clientQuotes = quotes.filter(q => q.clientId === client?._id) || [];
  const clientDocuments = documents.filter(doc => doc.clientId === client?._id) || [];
  
  const calculateTotal = (item) => {
    const subtotal = item.items?.reduce((sum, i) => sum + (i.quantity * i.price), 0) || 0;
    const discount = subtotal * (item.discountPercentage || 0) / 100;
    const afterDiscount = subtotal - discount;
    const tax = settings?.taxExempt ? 0 : afterDiscount * (settings?.taxPercentage || 10) / 100;
    return afterDiscount + tax;
  };

  const totalRevenue = clientInvoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + calculateTotal(inv), 0);
  
  const outstandingAmount = clientInvoices
    .filter(inv => inv.status === 'sent' || inv.status === 'overdue')
    .reduce((sum, inv) => sum + calculateTotal(inv), 0);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addFilesToPending(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFilesToPending(files);
    e.target.value = '';
  };

  const addFilesToPending = (files) => {
    const validFiles = files.filter(file => {
      const allowedTypes = [
        'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg', 'image/png', 'image/gif', 'text/plain', 'text/csv'
      ];
      return allowedTypes.includes(file.type) && file.size <= 10 * 1024 * 1024;
    });

    const newPendingFiles = validFiles.map(file => ({
      id: `pending_${Date.now()}_${Math.random()}`,
      file,
      label: '',
      description: '',
      isConfidential: false
    }));

    setPendingFiles(prev => [...prev, ...newPendingFiles]);
  };

  const updatePendingFile = (fileId, updates) => {
    setPendingFiles(prev => 
      prev.map(pf => pf._id === fileId ? { ...pf, ...updates } : pf)
    );
  };

  const removePendingFile = (fileId) => {
    setPendingFiles(prev => prev.filter(pf => pf._id !== fileId));
  };

  const uploadAllPendingFiles = async () => {
    if (!pendingFiles.length || !client?._id) return;

    const filesToUpload = pendingFiles.filter(pf => pf.label.trim());
    if (filesToUpload.length === 0) {
      alert('Please add labels to at least one file before uploading');
      return;
    }

    setUploadingFile(true);
    let uploadedCount = 0;
    const errors = [];

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const pendingFile = filesToUpload[i];
        
        try {
          setUploadProgress(prev => ({
            ...prev,
            [pendingFile._id]: { name: pendingFile.file.name, progress: 0, status: 'uploading' }
          }));

          await onUploadDocument(client._id, pendingFile.file, {
            label: pendingFile.label,
            description: pendingFile.description,
            isConfidential: pendingFile.isConfidential
          });

          uploadedCount++;
          setUploadProgress(prev => ({
            ...prev,
            [pendingFile._id]: { name: pendingFile.file.name, progress: 100, status: 'completed' }
          }));
        } catch (error) {
          errors.push(`${pendingFile.file.name}: ${error.message}`);
          setUploadProgress(prev => ({
            ...prev,
            [pendingFile._id]: { name: pendingFile.file.name, progress: 0, status: 'error', error: error.message }
          }));
        }
      }

      if (uploadedCount > 0) {
        setPendingFiles(prev => prev.filter(pf => !filesToUpload.includes(pf)));
      }

      if (errors.length > 0) {
        alert(`Some files failed to upload:\n${errors.join('\n')}`);
      } else if (uploadedCount === filesToUpload.length) {
        setTimeout(() => setUploadProgress({}), 2000);
      }
    } catch (error) {
      alert('Failed to upload documents: ' + error.message);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await onDeleteDocument(documentId);
      } catch (error) {
        alert('Failed to delete document: ' + error.message);
      }
    }
  };

  const handleUpdateDocument = async (documentId, updates) => {
    try {
      await onUpdateDocument(documentId, updates);
    } catch (error) {
      alert('Failed to update document: ' + error.message);
    }
  };

  const categories = [
    'General',
    'Corporate',
    'Small Business',
    'Freelancer',
    'Non-profit',
    'Government',
    'Education',
    'Healthcare',
    'Technology',
    'Construction',
    'Retail',
    'Other'
  ];

  const paymentTermsOptions = [
    'Due on Receipt',
    'Net 7',
    'Net 15',
    'Net 30',
    'Net 45',
    'Net 60',
    'Net 90'
  ];

  const currencyOptions = [
    { value: '£', label: '£ (GBP)' },
    { value: '$', label: '$ (USD)' },
    { value: '€', label: '€ (EUR)' },
    { value: '¥', label: '¥ (JPY)' },
    { value: 'C$', label: 'C$ (CAD)' },
    { value: 'A$', label: 'A$ (AUD)' }
  ];

  const documentLabels = [
    'Contract',
    'Agreement',
    'Invoice',
    'Receipt',
    'Certificate',
    'License',
    'Insurance',
    'Tax Document',
    'Legal Document',
    'Correspondence',
    'Proposal',
    'Specification',
    'Other'
  ];

return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={client ? `Edit Client: ${client.name}` : 'Add New Client'}
      size="xlarge"
      footer={
        <div className="flex gap-3">
          <button 
            type="button" 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" 
            onClick={onClose}
          >
            Cancel
          </button>
          {activeTab === 'details' && (
            <button 
              type="submit" 
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSubmit}
            >
              {client ? 'Update Client' : 'Add Client'}
            </button>
          )}
        </div>
      }
    >
      {/* Tabs */}
      <div className="p-0">
        <nav className="flex border-b border-gray-200">
              <button
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'details' 
                    ? 'border-blue-500 text-blue-600 bg-blue-50' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('details')}
                type="button"
              >
                <i className="fas fa-user mr-2"></i>
                Client Details
              </button>
              {client && (
                <>
                  <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'documents' 
                        ? 'border-blue-500 text-blue-600 bg-blue-50' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('documents')}
                    type="button"
                  >
                    <i className="fas fa-file-alt mr-2"></i>
                    Documents ({clientDocuments.length})
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'history' 
                        ? 'border-blue-500 text-blue-600 bg-blue-50' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('history')}
                    type="button"
                  >
                    <i className="fas fa-history mr-2"></i>
                    History ({clientInvoices.length + clientQuotes.length})
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'stats' 
                        ? 'border-blue-500 text-blue-600 bg-blue-50' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('stats')}
                    type="button"
                  >
                    <i className="fas fa-chart-bar mr-2"></i>
                    Statistics
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === 'recipients' 
                        ? 'border-blue-500 text-blue-600 bg-blue-50' 
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab('recipients')}
                    type="button"
                  >
                    <i className="fas fa-envelope mr-2"></i>
                    Recipients ({recipients.length})
                  </button>
                </>
              )}
            </nav>

        <div className="p-6">
              {activeTab === 'details' && (
                <div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h6 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Basic Information</h6>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Company/Client Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                          <input
                            type="text"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.contactPerson}
                            onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                            placeholder="Primary contact name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                          <input
                            type="url"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.website}
                            onChange={(e) => setFormData({...formData, website: e.target.value})}
                            placeholder="https://example.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                          <textarea
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            rows="3"
                            value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h6 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Business Details</h6>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Company Number</label>
                            <input
                              type="text"
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={formData.companyNumber}
                              onChange={(e) => setFormData({...formData, companyNumber: e.target.value})}
                              placeholder="12345678"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                            <input
                              type="text"
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={formData.vatNumber}
                              onChange={(e) => setFormData({...formData, vatNumber: e.target.value})}
                              placeholder="GB123456789"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.category}
                            onChange={(e) => setFormData({...formData, category: e.target.value})}
                          >
                            {categories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                            <select
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={formData.paymentTerms}
                              onChange={(e) => setFormData({...formData, paymentTerms: e.target.value})}
                            >
                              {paymentTermsOptions.map(term => (
                                <option key={term} value={term}>{term}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Currency</label>
                            <select
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={formData.currency}
                              onChange={(e) => setFormData({...formData, currency: e.target.value})}
                            >
                              {currencyOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex items-center">
                            <input
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              type="checkbox"
                              id="isActive"
                              checked={formData.isActive}
                              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                            />
                            <label className="ml-2 text-sm text-gray-900 cursor-pointer" htmlFor="isActive">
                              Active Client
                            </label>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-start">
                            <input
                              className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              type="checkbox"
                              id="defaultAutoPayEnabled"
                              checked={formData.defaultAutoPayEnabled}
                              onChange={(e) => setFormData({...formData, defaultAutoPayEnabled: e.target.checked})}
                            />
                            <label className="ml-2 cursor-pointer" htmlFor="defaultAutoPayEnabled">
                              <span className="block text-sm text-gray-900">Collect recurring invoices by Direct Debit</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Each new recurring invoice is collected automatically through the client's GoCardless mandate.
                                Without an active mandate, invoices are sent with bank details as usual.
                              </span>
                            </label>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                          <textarea
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            rows="4"
                            value={formData.notes}
                            onChange={(e) => setFormData({...formData, notes: e.target.value})}
                            placeholder="Internal notes about this client..."
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                          <input
                            type="password"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            placeholder="User password (Optional)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'documents' && client && (
                <div>
                  {/* Upload Section */}
                  <div className="bg-white shadow rounded-lg mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h6 className="text-lg font-medium text-gray-900 flex items-center">
                        <i className="fas fa-upload mr-2"></i>
                        Upload Documents
                      </h6>
                    </div>
                    <div className="p-6">
                      {/* Drag and Drop Zone */}
                      <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 cursor-pointer transition-colors ${
                          dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <i className="fas fa-cloud-upload-alt text-4xl mb-4 text-gray-400"></i>
                        <p className="text-lg font-medium text-gray-700 mb-2">
                          <strong>Drag and drop files here</strong> or{' '}
                          <label className="text-blue-600 hover:text-blue-700 cursor-pointer">
                            click to browse
                            <input
                              type="file"
                              className="hidden"
                              onChange={handleFileSelect}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv"
                              multiple
                            />
                          </label>
                        </p>
                        <p className="text-sm text-gray-500">
                          Supports: PDF, Word, Excel, Images, Text files (max 10MB each)
                        </p>
                      </div>

                      {/* Pending Files List */}
                      {pendingFiles.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-lg font-medium text-gray-900 mb-4">Files to Upload ({pendingFiles.length})</h6>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label *</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confidential</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {pendingFiles.map(pf => (
                                  <tr key={pf._id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <i className={`fas fa-${
                                          pf.file.type?.includes('pdf') ? 'file-pdf text-red-500' :
                                          pf.file.type?.includes('image') ? 'file-image text-green-500' :
                                          pf.file.type?.includes('word') || pf.file.type?.includes('document') ? 'file-word text-blue-500' :
                                          pf.file.type?.includes('excel') || pf.file.type?.includes('spreadsheet') ? 'file-excel text-green-500' :
                                          'file-alt text-gray-500'
                                        } mr-3`}></i>
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">{pf.file.name}</div>
                                          <div className="text-sm text-gray-500">{formatFileSize(pf.file.size)}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <select
                                        className="block w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        value={pf.label}
                                        onChange={(e) => updatePendingFile(pf._id, { label: e.target.value })}
                                      >
                                        <option value="">Select label...</option>
                                        {documentLabels.map(label => (
                                          <option key={label} value={label}>{label}</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <input
                                        type="text"
                                        className="block w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        value={pf.description}
                                        onChange={(e) => updatePendingFile(pf._id, { description: e.target.value })}
                                        placeholder="Brief description..."
                                      />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <input
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        type="checkbox"
                                        checked={pf.isConfidential}
                                        onChange={(e) => updatePendingFile(pf._id, { isConfidential: e.target.checked })}
                                      />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <button
                                        className="inline-flex items-center justify-center w-8 h-8 text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        onClick={() => removePendingFile(pf._id)}
                                        title="Remove file"
                                      >
                                        <i className="fas fa-times text-xs"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex justify-between items-center mt-4">
                            <div className="text-sm text-gray-500">
                              {pendingFiles.filter(pf => pf.label.trim()).length} of {pendingFiles.length} files ready to upload
                            </div>
                            <button
                              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={uploadAllPendingFiles}
                              disabled={uploadingFile || pendingFiles.filter(pf => pf.label.trim()).length === 0}
                            >
                              <i className="fas fa-upload mr-2"></i>
                              Upload {pendingFiles.filter(pf => pf.label.trim()).length} Files
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Upload Progress */}
                      {uploadingFile && Object.keys(uploadProgress).length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-lg font-medium text-gray-900 mb-4">Upload Progress</h6>
                          {Object.entries(uploadProgress).map(([fileId, progress]) => (
                            <div key={fileId} className="mb-3">
                              <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-700 truncate max-w-xs">
                                  {progress.name}
                                </div>
                                <div className="text-sm">
                                  {progress.status === 'uploading' && (
                                    <span className="text-blue-600">
                                      <i className="fas fa-spinner fa-spin mr-1"></i>
                                      Uploading...
                                    </span>
                                  )}
                                  {progress.status === 'completed' && (
                                    <span className="text-green-600">
                                      <i className="fas fa-check mr-1"></i>
                                      Uploaded
                                    </span>
                                  )}
                                  {progress.status === 'error' && (
                                    <span className="text-red-600">
                                      <i className="fas fa-times mr-1"></i>
                                      Failed
                                    </span>
                                  )}
                                </div>
                              </div>
                              {progress.status === 'error' && (
                                <div className="text-sm text-red-600 mt-1">{progress.error}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Documents List */}
                  <div className="bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h6 className="text-lg font-medium text-gray-900 flex items-center">
                        <i className="fas fa-folder mr-2"></i>
                        Documents ({clientDocuments.length})
                      </h6>
                    </div>
                    <div className="overflow-hidden">
                      {clientDocuments.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <i className="fas fa-file-alt text-4xl mb-4 opacity-50"></i>
                          <p className="text-lg">No documents uploaded yet</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {clientDocuments.map(doc => (
                                <tr key={doc._id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <i className={`fas fa-${
                                        doc.mimeType?.includes('pdf') ? 'file-pdf text-red-500' :
                                        doc.mimeType?.includes('image') ? 'file-image text-green-500' :
                                        doc.mimeType?.includes('word') || doc.mimeType?.includes('document') ? 'file-word text-blue-500' :
                                        doc.mimeType?.includes('excel') || doc.mimeType?.includes('spreadsheet') ? 'file-excel text-green-500' :
                                        'file-alt text-gray-500'
                                      } mr-3`}></i>
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">{doc.documentName}</div>
                                        <div className="text-sm text-gray-500">{doc.originalFilename}</div>
                                        {doc.isConfidential && (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                                            <i className="fas fa-lock mr-1"></i>Confidential
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{doc.label}</span>
                                    {doc.description && (
                                      <div className="text-sm text-gray-500 mt-1">{doc.description}</div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatFileSize(doc.fileSize)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(doc.createdAt)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <button
                                        className="inline-flex items-center justify-center w-8 h-8 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        onClick={() => onDownloadDocument(doc._id)}
                                        title="Download"
                                      >
                                        <i className="fas fa-download text-xs"></i>
                                      </button>
                                      <button
                                        className="inline-flex items-center justify-center w-8 h-8 text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        onClick={() => handleDeleteDocument(doc._id)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash text-xs"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'history' && client && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6 text-center">
                      <div className="text-2xl font-bold text-blue-600">{clientInvoices.length}</div>
                      <div className="text-sm text-gray-500">Total Invoices</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6 text-center">
                      <div className="text-2xl font-bold text-cyan-600">{clientQuotes.length}</div>
                      <div className="text-sm text-gray-500">Total Quotes</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6 text-center">
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</div>
                      <div className="text-sm text-gray-500">Total Revenue</div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {[...clientInvoices, ...clientQuotes]
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map(item => {
                            const isInvoice = item.number?.startsWith(settings?.invoicePrefix || 'INV');
                            return (
                              <tr key={item._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.number}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isInvoice ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'
                                  }`}>
                                    {isInvoice ? 'Invoice' : 'Quote'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(item.date)}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    item.status === 'paid' ? 'bg-green-100 text-green-800' : 
                                    item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                                    item.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {item.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(calculateTotal(item))}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'stats' && client && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h6 className="text-lg font-medium text-gray-900">Financial Summary</h6>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Total Revenue:</span>
                        <span className="font-semibold text-green-600">{formatCurrency(totalRevenue)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Outstanding:</span>
                        <span className="font-semibold text-yellow-600">{formatCurrency(outstandingAmount)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Average Invoice:</span>
                        <span className="font-semibold text-gray-900">
                          {clientInvoices.length > 0 
                            ? formatCurrency(clientInvoices.reduce((sum, inv) => sum + calculateTotal(inv), 0) / clientInvoices.length)
                            : formatCurrency(0)
                          }
                        </span>
                      </div>
                      <hr className="border-gray-200" />
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Payment Terms:</span>
                        <span className="font-semibold text-gray-900">{formData.paymentTerms}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h6 className="text-lg font-medium text-gray-900">Activity Summary</h6>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Total Invoices:</span>
                        <span className="font-semibold text-gray-900">{clientInvoices.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Paid Invoices:</span>
                        <span className="font-semibold text-green-600">
                          {clientInvoices.filter(inv => inv.status === 'paid').length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Overdue Invoices:</span>
                        <span className="font-semibold text-red-600">
                          {clientInvoices.filter(inv => inv.status === 'overdue').length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Total Quotes:</span>
                        <span className="font-semibold text-gray-900">{clientQuotes.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Accepted Quotes:</span>
                        <span className="font-semibold text-green-600">
                          {clientQuotes.filter(q => q.status === 'accepted').length}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Documents:</span>
                        <span className="font-semibold text-cyan-600">{clientDocuments.length}</span>
                      </div>
                      <hr className="border-gray-200" />
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">First Invoice:</span>
                        <span className="font-semibold text-gray-900">
                          {clientInvoices.length > 0 
                            ? formatDate(Math.min(...clientInvoices.map(inv => new Date(inv.date))))
                            : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'recipients' && client && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h6 className="text-lg font-medium text-gray-900">Email Recipients</h6>
                    <button 
                      className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => setShowAddRecipientForm(true)}
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Add Recipient
                    </button>
                  </div>

                  {/* Add/Edit Recipient Form */}
                  {showAddRecipientForm && (
                    <div className="bg-white shadow rounded-lg mb-6">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h6 className="text-lg font-medium text-gray-900">
                          {editingRecipient ? 'Edit Recipient' : 'Add New Recipient'}
                        </h6>
                      </div>
                      <div className="p-6">
                        <form onSubmit={handleRecipientSubmit}>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={recipientFormData.name}
                                onChange={(e) => setRecipientFormData({...recipientFormData, name: e.target.value})}
                                placeholder="Recipient name"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="email"
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                value={recipientFormData.email}
                                onChange={(e) => setRecipientFormData({...recipientFormData, email: e.target.value})}
                                placeholder="recipient@example.com"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Primary</label>
                              <div className="mt-2">
                                <div className="flex items-center">
                                  <input
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    type="checkbox"
                                    checked={recipientFormData.isPrimary}
                                    onChange={(e) => setRecipientFormData({...recipientFormData, isPrimary: e.target.checked})}
                                  />
                                  <label className="ml-2 text-sm text-gray-900 cursor-pointer">
                                    Primary
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                              <div className="mt-2">
                                <div className="flex items-center">
                                  <input
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    type="checkbox"
                                    checked={recipientFormData.isActive}
                                    onChange={(e) => setRecipientFormData({...recipientFormData, isActive: e.target.checked})}
                                  />
                                  <label className="ml-2 text-sm text-gray-900 cursor-pointer">
                                    Active
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-3 mt-6">
                            <button 
                              type="submit" 
                              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <i className="fas fa-save mr-2"></i>
                              {editingRecipient ? 'Update' : 'Add'} Recipient
                            </button>
                            <button 
                              type="button" 
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              onClick={cancelRecipientForm}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}

                  {/* Recipients List */}
                  <div className="bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h6 className="text-lg font-medium text-gray-900 flex items-center">
                        <i className="fas fa-users mr-2"></i>
                        Recipients List ({recipients.length})
                      </h6>
                    </div>
                    <div className="overflow-hidden">
                      {loadingRecipients ? (
                        <div className="text-center py-12">
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Loading recipients...
                        </div>
                      ) : recipients.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <i className="fas fa-envelope text-4xl mb-4 opacity-50"></i>
                          <p className="text-lg">No email recipients configured yet</p>
                          <p className="text-sm">Add recipients to receive invoice and quote notifications</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {recipients.map(recipient => (
                                <tr key={recipient._id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <i className="fas fa-user mr-3 text-gray-400"></i>
                                      <span className="text-sm font-medium text-gray-900">{recipient.name}</span>
                                      {recipient.isPrimary && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                                          <i className="fas fa-star mr-1"></i>
                                          Primary
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <a href={`mailto:${recipient.email}`} className="text-blue-600 hover:text-blue-800">
                                      {recipient.email}
                                    </a>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      recipient.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {recipient.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <button
                                        className="inline-flex items-center justify-center w-8 h-8 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        onClick={() => startEditRecipient(recipient)}
                                        title="Edit recipient"
                                      >
                                        <i className="fas fa-edit text-xs"></i>
                                      </button>
                                      <button
                                        className="inline-flex items-center justify-center w-8 h-8 text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => deleteRecipient(recipient._id)}
                                        title="Delete recipient"
                                        disabled={recipients.length <= 1}
                                      >
                                        <i className="fas fa-trash text-xs"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    {recipients.length > 0 && (
                      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                          <i className="fas fa-info-circle mr-1"></i>
                          Recipients will receive email notifications for invoices and quotes.
                          {recipients.length <= 1 && ' At least one recipient must exist.'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
        </div>
      </div>
    </Modal>
  );
};

export default ClientModal;